import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { Composer } from "@/components/Composer";
import { PostCard } from "@/components/PostCard";
import { LiveBanner } from "@/components/LiveBanner";

const Sidebar = () => {
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  useEffect(() => {
    api.get("/events").then((r) => {
      const now = new Date().toISOString();
      setEvents(r.data.events.filter((e) => e.start >= now).slice(0, 3));
    }).catch(() => {});
    api.get("/members").then((r) => setCount(r.data.length)).catch(() => {});
  }, []);
  return (
    <aside className="space-y-5">
      <div className="hb-card overflow-hidden">
        <img src="/brand/lunch.jpg" alt="Hey Boo! community" className="h-36 w-full object-cover" />
        <div className="p-5">
          <h3 className="font-display text-xl">Hey Boo! Members Only</h3>
          <p className="text-sm text-[var(--hb-muted)] mt-1">Where female founders build, grow, and GO ALL IN.</p>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-display text-3xl" data-testid="sidebar-member-count">{count}</span>
            <span className="label-accent">Members</span>
          </div>
        </div>
      </div>
      <div className="hb-card p-5">
        <div className="flex items-center justify-between">
          <span className="label-accent">Coming up</span>
          <Link to="/events" className="text-xs underline underline-offset-4" data-testid="sidebar-all-events">All events</Link>
        </div>
        {events.length === 0 && <p className="text-sm text-[var(--hb-muted)] mt-3">No upcoming events yet.</p>}
        {events.map((e) => (
          <div key={e.id} className="flex gap-3 mt-4" data-testid={`sidebar-event-${e.id}`}>
            <div className="w-12 shrink-0 rounded-xl bg-[var(--hb-lime)] text-center py-1.5">
              <div className="font-accent text-[10px] font-bold uppercase">{format(new Date(e.start), "MMM")}</div>
              <div className="font-display text-lg leading-none">{format(new Date(e.start), "d")}</div>
            </div>
            <div className="text-sm leading-snug">{e.title}</div>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default function CommunityPage() {
  const [posts, setPosts] = useState(null);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("");

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data));
  }, []);
  useEffect(() => {
    api.get("/posts", { params: { category: active } }).then((r) => setPosts(r.data));
  }, [active]);

  const update = (p) =>
    setPosts(
      posts
        .map((x) => (x.id === p.id ? p : x))
        .sort((a, b) => (b.pinned - a.pinned) || b.created_at.localeCompare(a.created_at))
    );

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-8" data-testid="community-page">
      <section className="space-y-5 min-w-0">
        <LiveBanner />
        <Composer categories={categories} onPosted={(p) => setPosts([p, ...posts])} />
        <div className="flex gap-2 overflow-x-auto no-scrollbar md:flex-wrap md:overflow-visible" data-testid="category-filters">
          {["", ...categories].map((c) => (
            <button key={c || "all"} onClick={() => setActive(c)} data-testid={`category-filter-${c || "all"}`}
              className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap border transition-colors ${
                active === c ? "bg-[var(--hb-ink)] text-white border-[var(--hb-ink)]" : "bg-white border-[var(--hb-line)] hover:border-[var(--hb-ink)]"
              }`}>
              {c || "All"}
            </button>
          ))}
        </div>
        {posts === null && <div className="text-[var(--hb-muted)]">Loading the feed...</div>}
        {posts?.length === 0 && (
          <div className="hb-card p-10 text-center text-[var(--hb-muted)]" data-testid="feed-empty">
            Nothing here yet. Be the first to post!
          </div>
        )}
        {posts?.map((p) => (
          <PostCard key={p.id} post={p} onChange={update} onDelete={(id) => setPosts(posts.filter((x) => x.id !== id))} />
        ))}
      </section>
      <Sidebar />
    </div>
  );
}
